// Phase 1A Step 34 — Calculator weekly path chain track + bottom meta.
// Extracted from Calculator.tsx for LOC ceiling. Renders the staggered
// week-by-week reveal showing stake/win/keep per week.

import { useEffect, useRef, useState } from "react";
import type { ChainRow } from "./calc-utils.ts";
import { compact } from "./calc-utils.ts";

interface CalculatorChainTrackProps {
  chain: ChainRow[];
  weeks: number;
  mode: "protected" | "full";
}

export function CalculatorChainTrack({
  chain,
  weeks,
  mode,
}: CalculatorChainTrackProps): JSX.Element {
  // Sequential build on first load — staggered week reveals.
  const [revealedUpTo, setRevealedUpTo] = useState(0);
  const builtOnceRef = useRef(false);
  useEffect(() => {
    if (builtOnceRef.current) {
      setRevealedUpTo(weeks);
      return;
    }
    builtOnceRef.current = true;
    let i = 0;
    setRevealedUpTo(0);
    const id = setInterval(() => {
      i += 1;
      setRevealedUpTo(i);
      if (i >= weeks) clearInterval(id);
    }, 130);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (builtOnceRef.current) setRevealedUpTo(weeks);
  }, [weeks]);

  return (
    <>
      <div
        className="eyebrow-muted"
        style={{ marginBottom: 10, display: "flex", justifyContent: "space-between" }}
      >
        <span>Weekly path</span>
        <span style={{ color: "var(--text-dim)" }}>Stake → Win → Keep / Restake</span>
      </div>
      <div
        className="sz-l-calc-track"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.min(weeks, 12)}, 1fr)`,
          gap: 4,
          marginBottom: 6,
        }}
      >
        {chain.map((c, i) => {
          const visible = i < revealedUpTo;
          const isFinal = i === chain.length - 1;
          return (
            <div
              key={i}
              style={{
                textAlign: "left",
                paddingTop: 8,
                borderTop: `1px solid ${isFinal ? "var(--accent)" : "var(--border-bright)"}`,
                opacity: visible ? 1 : 0,
                transform: visible ? "none" : "translateY(6px)",
                transition: "opacity .35s ease, transform .35s ease",
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 9,
                  letterSpacing: "0.1em",
                  color: isFinal ? "var(--accent)" : "var(--text-dim)",
                  marginBottom: 4,
                }}
              >
                W{c.week.toString().padStart(2, "0")}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: isFinal ? "var(--accent)" : "var(--text)",
                  fontWeight: isFinal ? 600 : 400,
                }}
              >
                {compact(c.win)}
              </div>
              {mode === "protected" && (
                <div
                  className="mono"
                  style={{ fontSize: 9, color: "var(--success)", marginTop: 2 }}
                >
                  +{compact(c.kept)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 24,
          paddingTop: 18,
          borderTop: "1px solid var(--border)",
          fontFamily: "var(--mono)",
          fontSize: 10,
          letterSpacing: "0.1em",
          color: "var(--text-muted)",
          textTransform: "uppercase",
        }}
      >
        <span>Each leg must hit · One miss breaks the chain</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="live-dot" />
          <span>Engine online</span>
        </span>
      </div>
    </>
  );
}
